import TPEAction from 'src/models/common/TPEAction';
import {
    SegmentTablesState,
    SegmentColumnDraft,
    SegmentRowsPage,
    initialCreateWizardState,
} from 'src/services/segment-cdt/SegmentTablesState';
import {
    CDTSegmentType,
    ColumnDataType,
    ColumnDef,
    ColumnSourceType,
    PullStatusResponse,
    SegmentAggregates,
    SegmentPeriodInfo,
    SegmentTableDefinition,
} from 'src/models/segment-cdt/SegmentCdtTypes';
import ReactTableFilter from 'src/models/common/ReactTableFilter';

export const ACTIONS = Object.freeze({
    // Bumped to re-run the list fetch (e.g. after a create). The list view keys its request off
    // refreshToken so a new fetch fires.
    REFRESH: new TPEAction('REFRESH'),
    // Create-wizard lifecycle. Wizard children read state.createWizard from
    // SegmentTablesContext and dispatch these directly.
    OPEN_CREATE_WIZARD: new TPEAction('OPEN_CREATE_WIZARD'),
    CLOSE_CREATE_WIZARD: new TPEAction('CLOSE_CREATE_WIZARD'),
    SET_WIZARD_STEP: new TPEAction('SET_WIZARD_STEP'),
    SET_DRAFT_META: new TPEAction('SET_DRAFT_META'),
    SET_CHOOSING: new TPEAction('SET_CHOOSING'),
    START_COLUMN_EDITOR: new TPEAction('START_COLUMN_EDITOR'),
    SET_COLUMN_DRAFT: new TPEAction('SET_COLUMN_DRAFT'),
    COMMIT_COLUMN: new TPEAction('COMMIT_COLUMN'),
    CANCEL_COLUMN_EDITOR: new TPEAction('CANCEL_COLUMN_EDITOR'),
    REMOVE_COLUMN: new TPEAction('REMOVE_COLUMN'),
    MOVE_COLUMN: new TPEAction('MOVE_COLUMN'),
    SET_CREATING: new TPEAction('SET_CREATING'),
    SET_CREATE_ERROR: new TPEAction('SET_CREATE_ERROR'),
    // Starts a fresh "add columns to an existing table" session. Reuses the createWizard editor slice
    // (chooser + columnEditor + queued columns), which is otherwise unused on the detail route, seeding
    // the segment so the GL / derived CoA lookups resolve. Not a create: no tableName, and it commits
    // via AddSegmentColumns rather than CreateSegmentDataTable.
    START_ADD_COLUMNS_SESSION: new TPEAction('START_ADD_COLUMNS_SESSION'),
    // Detail (data-viewer) route. Its view mounts its own reducer instance and reads state.detail.
    SET_DETAIL_TABLE_ID: new TPEAction('SET_DETAIL_TABLE_ID'),
    SET_DETAIL_DEFINITION: new TPEAction('SET_DETAIL_DEFINITION'),
    SET_DETAIL_PERIODS: new TPEAction('SET_DETAIL_PERIODS'),
    SELECT_DETAIL_PERIOD: new TPEAction('SELECT_DETAIL_PERIOD'),
    SET_DETAIL_PAGE: new TPEAction('SET_DETAIL_PAGE'),
    SET_DETAIL_SORT: new TPEAction('SET_DETAIL_SORT'),
    SET_DETAIL_FILTERS: new TPEAction('SET_DETAIL_FILTERS'),
    SET_DETAIL_SEARCH_RESULT: new TPEAction('SET_DETAIL_SEARCH_RESULT'),
    SET_DETAIL_AGGREGATES: new TPEAction('SET_DETAIL_AGGREGATES'),
    SET_DETAIL_PULL_STATUS: new TPEAction('SET_DETAIL_PULL_STATUS'),
    REFRESH_DETAIL_ROWS: new TPEAction('REFRESH_DETAIL_ROWS'),
});

export function segmentTablesReducer(state: SegmentTablesState, action: TPEAction): SegmentTablesState {
    const cw = state.createWizard;
    switch (action.type) {
        case ACTIONS.REFRESH.type:
            return { ...state, refreshToken: state.refreshToken + 1 };

        case ACTIONS.OPEN_CREATE_WIZARD.type:
            return { ...state, createWizard: { ...initialCreateWizardState, visible: true } };

        case ACTIONS.CLOSE_CREATE_WIZARD.type:
            return { ...state, createWizard: { ...initialCreateWizardState, visible: false } };

        case ACTIONS.SET_WIZARD_STEP.type:
            return { ...state, createWizard: { ...cw, currentStep: action.payload as number } };

        case ACTIONS.SET_DRAFT_META.type: {
            // payload: partial { tableName?, segmentType?, segmentCustomCoaId?, refreshCadence? }
            // Custom CoA definitions are scoped per segment, so a segment change invalidates the
            // previously-picked table-level segmentCustomCoaId — clear it. The segment can only change
            // while no columns exist (SegmentMetaStep locks it once columns are added), so a committed
            // column's segment-scoped pull-criteria data keys can never go stale here.
            const patch = action.payload;
            const segmentChanged = patch.segmentType !== undefined && patch.segmentType !== cw.segmentType;
            return {
                ...state,
                createWizard: {
                    ...cw,
                    ...patch,
                    ...(segmentChanged ? { segmentCustomCoaId: undefined } : {}),
                },
            };
        }

        case ACTIONS.SET_CHOOSING.type:
            return { ...state, createWizard: { ...cw, choosing: action.payload as boolean } };

        case ACTIONS.START_COLUMN_EDITOR.type: {
            // payload: { sourceType?: ColumnSourceType }. Columns are add-only; there is no edit path.
            // Picking a type leaves the chooser, so clear `choosing` as the editor opens.
            const { sourceType } = action.payload;
            const draft: SegmentColumnDraft = {
                sourceType,
                // PULL_GL columns are always NUMBER per the contract; seed it so the form never has to.
                dataType: sourceType === ColumnSourceType.PULL_GL ? ColumnDataType.NUMBER : undefined,
            };
            return { ...state, createWizard: { ...cw, choosing: false, columnEditor: { draft } } };
        }

        case ACTIONS.SET_COLUMN_DRAFT.type: {
            // payload: partial SegmentColumnDraft; a nested pullCriteria is shallow-merged.
            if (!cw.columnEditor) return state;
            const patch = action.payload as Partial<SegmentColumnDraft>;
            const merged: SegmentColumnDraft = { ...cw.columnEditor.draft, ...patch };
            if (patch.pullCriteria) {
                merged.pullCriteria = { ...cw.columnEditor.draft.pullCriteria, ...patch.pullCriteria };
            }
            // Same shallow-merge for derivedConfig: a derived form sets its reference fields in separate
            // dispatches (pctColumnId, then valueColumnId), so without merging the second would drop the
            // first and the column could never satisfy its two-reference requirement.
            if (patch.derivedConfig) {
                merged.derivedConfig = { ...cw.columnEditor.draft.derivedConfig, ...patch.derivedConfig };
            }
            return { ...state, createWizard: { ...cw, columnEditor: { ...cw.columnEditor, draft: merged } } };
        }

        case ACTIONS.COMMIT_COLUMN.type: {
            // payload: a fully-built ColumnDef (the form assigns columnId from the display name).
            // Columns are add only, so a committed column is always appended.
            const col = action.payload as ColumnDef;
            return { ...state, createWizard: { ...cw, columns: [...cw.columns, col], columnEditor: undefined } };
        }

        case ACTIONS.CANCEL_COLUMN_EDITOR.type:
            return { ...state, createWizard: { ...cw, columnEditor: undefined } };

        case ACTIONS.REMOVE_COLUMN.type:
            return {
                ...state,
                createWizard: { ...cw, columns: cw.columns.filter((_, i) => i !== (action.payload as number)) },
            };

        case ACTIONS.MOVE_COLUMN.type: {
            // payload: { index: number, dir: -1 | 1 } — reorder before create (order == value_order).
            const { index, dir } = action.payload;
            const target = index + dir;
            if (target < 0 || target >= cw.columns.length) {
                return state;
            }
            const columns = [...cw.columns];
            [columns[index], columns[target]] = [columns[target], columns[index]];
            return { ...state, createWizard: { ...cw, columns } };
        }

        case ACTIONS.SET_CREATING.type:
            return { ...state, createWizard: { ...cw, creating: action.payload as boolean } };

        case ACTIONS.SET_CREATE_ERROR.type:
            return { ...state, createWizard: { ...cw, createError: action.payload as string } };

        case ACTIONS.START_ADD_COLUMNS_SESSION.type: {
            // payload: { segmentType? }. Reset the editor slice to empty and seed the segment so the
            // GL / derived CoA lookups resolve; `visible` stays false since the detail route renders no
            // create-wizard modal (the Add Columns modal owns its own visibility).
            const { segmentType } = (action.payload ?? {}) as { segmentType?: CDTSegmentType };
            return { ...state, createWizard: { ...initialCreateWizardState, segmentType } };
        }

        case ACTIONS.SET_DETAIL_TABLE_ID.type:
            return { ...state, detail: { ...state.detail, tableId: action.payload as string } };

        case ACTIONS.SET_DETAIL_DEFINITION.type:
            return { ...state, detail: { ...state.detail, definition: action.payload as SegmentTableDefinition } };

        case ACTIONS.SET_DETAIL_PERIODS.type: {
            // payload: SegmentPeriodInfo[]. Default the selected period to the latest (periods sorted
            // descending) only when nothing is selected yet, so a user's pick survives a refetch.
            const periods = (action.payload as SegmentPeriodInfo[]) ?? [];
            const latest = [...periods].sort((a, b) => (b.period ?? '').localeCompare(a.period ?? ''))[0];
            return {
                ...state,
                detail: { ...state.detail, periods, selectedPeriod: state.detail.selectedPeriod ?? latest?.period },
            };
        }

        case ACTIONS.SELECT_DETAIL_PERIOD.type:
            // A different period is an entirely different row set, so return to the first page and drop the
            // old period's rows — otherwise a failed search for the new period would leave the previous
            // period's rows showing under the new label (a misread), not just a banner. The totals are
            // per-period too, so they are dropped for the same reason: showing the old period's totals
            // under a new period's label would misstate the reconciliation.
            // The tracked pull goes with them: it belongs to the period it was triggered for, so leaving it
            // would report another period's pull as this one's progress.
            return { ...state, detail: { ...state.detail, selectedPeriod: action.payload as string, page: 1, searchResult: undefined, aggregates: undefined, pullStatus: undefined } };

        case ACTIONS.SET_DETAIL_PAGE.type:
            return { ...state, detail: { ...state.detail, page: action.payload as number } };

        case ACTIONS.SET_DETAIL_SORT.type: {
            // payload: { sortField?, sortOrder? }. Re-sorting reorders the whole set, so reset to page 1 and
            // clear the stale rows so a failed re-search can't show the old order under the new sort.
            const { sortField, sortOrder } = (action.payload ?? {}) as { sortField?: string; sortOrder?: string };
            return { ...state, detail: { ...state.detail, sortField, sortOrder, page: 1, searchResult: undefined } };
        }

        case ACTIONS.SET_DETAIL_FILTERS.type:
            // payload: ReactTableFilter[]. Filtering changes the result set, so return to page 1 and clear
            // the stale rows so a failed re-search can't show unfiltered rows under an active filter.
            return { ...state, detail: { ...state.detail, filters: (action.payload as ReactTableFilter[]) ?? [], page: 1, searchResult: undefined } };

        case ACTIONS.SET_DETAIL_SEARCH_RESULT.type:
            return {
                ...state,
                detail: { ...state.detail, searchResult: action.payload as SegmentRowsPage },
            };

        case ACTIONS.SET_DETAIL_AGGREGATES.type:
            // payload: SegmentAggregates | undefined. Totals are server-computed per column for the
            // selected period; they are never summed client-side.
            return {
                ...state,
                detail: { ...state.detail, aggregates: action.payload as SegmentAggregates },
            };

        case ACTIONS.SET_DETAIL_PULL_STATUS.type:
            // payload: PullStatusResponse | undefined. Written by the toolbar's poll on every tick and
            // cleared when it stops tracking, so the metadata row's indicator and the button always agree.
            // A terminal status is kept deliberately: it is what lets the row report the outcome
            // ("Completed" / "Failed") after the poll stops, rather than blanking the moment it ends.
            return {
                ...state,
                detail: { ...state.detail, pullStatus: action.payload as PullStatusResponse | undefined },
            };

        case ACTIONS.REFRESH_DETAIL_ROWS.type:
            // Bump to re-run the current period's row search after a write (GL pull / upload). Reset to
            // page 1 (a write can shrink the period below the current page) and clear the prior rows — as
            // the period/sort/filter cases do — so a failed post-write re-search can't leave stale rows
            // showing under a "N rows updated" banner. The loading gate shows a spinner during the gap.
            //
            // The same token re-fetches the period's totals, and the stale ones are cleared with the rows:
            // a GL pull is precisely the event that changes them, so leaving the pre-pull totals visible
            // under a "pull completed" banner would assert a reconciliation that no longer holds. Note the
            // sort/filter cases deliberately do NOT clear them — totals are period-wide, so re-sorting or
            // filtering a page does not change them.
            return {
                ...state,
                detail: {
                    ...state.detail,
                    refreshToken: state.detail.refreshToken + 1,
                    page: 1,
                    searchResult: undefined,
                    aggregates: undefined,
                },
            };

        default:
            console.warn(`No action found for ${action.type}. Returning unchanged state`);
            return state;
    }
}
